import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Badge, Spinner, Alert, Card, Tab, Nav } from 'react-bootstrap';
import { Clock, Plus, Trash2, CheckCircle2, ShieldAlert, Sun, Coffee } from 'lucide-react';
import { toast } from 'react-toastify';
import { 
    getDriverSchedule, 
    updateDriverSchedule, 
    checkUnavailabilityConflicts, 
    createDriverUnavailability, 
    deleteDriverUnavailability 
} from '../api';

const DAYS_OF_WEEK = [
    { key: "1", name: "Lunes" },
    { key: "2", name: "Martes" },
    { key: "3", name: "Miércoles" },
    { key: "4", name: "Jueves" },
    { key: "5", name: "Viernes" },
    { key: "6", name: "Sábado" },
    { key: "7", name: "Domingo" }
];

const DEFAULT_SCHEDULE = {
    "1": { active: true, start: "07:00", end: "19:00" },
    "2": { active: true, start: "07:00", end: "19:00" },
    "3": { active: true, start: "07:00", end: "19:00" },
    "4": { active: true, start: "07:00", end: "19:00" },
    "5": { active: true, start: "07:00", end: "19:00" },
    "6": { active: false, start: "08:00", end: "18:00" },
    "7": { active: false, start: "08:00", end: "18:00" }
};

const ABSENCE_REASONS = [
    "Vacaciones",
    "Mantención de Móvil / Taller",
    "Permiso Personal",
    "Licencia Médica",
    "Trámite Documentario",
    "Otro"
];

const DriverAvailabilityModal = ({ show, onHide, driverId = null, driverName = null, onSaved = null }) => {
    const [activeTab, setActiveTab] = useState('weekly');
    const [loading, setLoading] = useState(false);
    const [savingSchedule, setSavingSchedule] = useState(false);
    
    // Horario semanal
    const [weeklySchedule, setWeeklySchedule] = useState(DEFAULT_SCHEDULE);
    
    // Períodos de ausencia
    const [unavailabilities, setUnavailabilities] = useState([]);
    
    // Formulario de nueva ausencia
    const [showNewAbsenceForm, setShowNewAbsenceForm] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reason, setReason] = useState('Vacaciones');
    const [notes, setNotes] = useState('');
    const [checkingConflicts, setCheckingConflicts] = useState(false);
    const [savingAbsence, setSavingAbsence] = useState(false);
    const [conflicts, setConflicts] = useState(null);

    // Cargar datos al abrir modal
    useEffect(() => {
        if (show) {
            loadData();
            setShowNewAbsenceForm(false);
            setConflicts(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, driverId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getDriverSchedule(driverId);
            if (data?.weekly_schedule && Object.keys(data.weekly_schedule).length > 0) {
                setWeeklySchedule(data.weekly_schedule);
            } else {
                setWeeklySchedule(DEFAULT_SCHEDULE);
            }
            setUnavailabilities(data?.unavailabilities || []);
        } catch (error) {
            console.error("Error al cargar horario del conductor:", error);
        } finally {
            setLoading(false);
        }
    };

    // Toggle de día activo
    const handleToggleDay = (dayKey) => {
        setWeeklySchedule(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                active: !prev[dayKey]?.active
            }
        }));
    };

    // Cambio de hora inicio o fin
    const handleTimeChange = (dayKey, field, value) => {
        setWeeklySchedule(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                [field]: value
            }
        }));
    };

    // Copiar horario de Lunes a Viernes
    const copyMondayToFriday = () => {
        const mon = weeklySchedule["1"] || { active: true, start: "07:00", end: "19:00" };
        setWeeklySchedule(prev => ({
            ...prev,
            "1": { ...mon },
            "2": { ...mon },
            "3": { ...mon },
            "4": { ...mon },
            "5": { ...mon }
        }));
        toast.info("📋 Horario del lunes copiado a martes, miércoles, jueves y viernes.");
    };

    // Guardar horario semanal
    const handleSaveSchedule = async () => {
        setSavingSchedule(true);
        try {
            await updateDriverSchedule(weeklySchedule, driverId);
            toast.success("✅ Horario semanal guardado exitosamente.");
            if (onSaved) onSaved();
        } catch (error) {
            console.error("Error al guardar horario semanal:", error);
            toast.error("❌ Error al guardar horario semanal.");
        } finally {
            setSavingSchedule(false);
        }
    };

    // Iniciar creación de ausencia
    const handleStartNewAbsence = () => {
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
        setEndDate(today);
        setReason('Vacaciones');
        setNotes('');
        setConflicts(null);
        setShowNewAbsenceForm(true);
    };

    // Verificar conflictos antes de guardar ausencia
    const handleCheckConflictsAndSave = async () => {
        if (!startDate || !endDate) {
            toast.warning("Por favor selecciona la fecha de inicio y de término.");
            return;
        }
        if (endDate < startDate) {
            toast.warning("La fecha de término no puede ser anterior a la de inicio.");
            return;
        }

        setCheckingConflicts(true);
        try {
            const conflictRes = await checkUnavailabilityConflicts({
                start_date: startDate,
                end_date: endDate,
                reason,
                notes
            }, driverId);

            if (conflictRes.has_conflicts) {
                setConflicts(conflictRes.trips);
            } else {
                // Sin conflictos, guardar directamente
                await executeSaveAbsence();
            }
        } catch (error) {
            console.error("Error al chequear conflictos:", error);
            toast.error("Error al validar disponibilidad.");
        } finally {
            setCheckingConflicts(false);
        }
    };

    // Guardar ausencia confirmada
    const executeSaveAbsence = async () => {
        setSavingAbsence(true);
        try {
            await createDriverUnavailability({
                start_date: startDate,
                end_date: endDate,
                reason,
                notes
            }, driverId);
            toast.success("✅ Período de ausencia programado exitosamente.");
            setShowNewAbsenceForm(false);
            setConflicts(null);
            loadData();
            if (onSaved) onSaved();
        } catch (error) {
            console.error("Error al crear ausencia:", error);
            toast.error("❌ Error al programar la ausencia.");
        } finally {
            setSavingAbsence(false);
        }
    };

    // Eliminar ausencia
    const handleDeleteAbsence = async (id) => {
        if (!window.confirm("¿Seguro que deseas eliminar este período de ausencia?")) return;
        try {
            await deleteDriverUnavailability(id, driverId);
            toast.success("Período de ausencia eliminado.");
            loadData();
            if (onSaved) onSaved();
        } catch (error) {
            console.error("Error al eliminar ausencia:", error);
            toast.error("Error al eliminar la ausencia.");
        }
    };

    const formatDateStr = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="bg-light border-bottom">
                <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
                    <Clock className="text-primary" size={20} />
                    {driverName ? `Horario y Disponibilidad - ${driverName}` : "Mi Horario y Disponibilidad (EmiGo)"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0">
                <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                    <Nav variant="tabs" className="px-3 pt-2 bg-light border-bottom">
                        <Nav.Item>
                            <Nav.Link eventKey="weekly" className="fw-semibold small d-flex align-items-center gap-1">
                                <Sun size={16} /> Jornada Semanal Habitual
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="absences" className="fw-semibold small d-flex align-items-center gap-1">
                                <Coffee size={16} /> Vacaciones y Ausencias
                                {unavailabilities.length > 0 && (
                                    <Badge bg="secondary" pill className="ms-1" style={{ fontSize: '10px' }}>
                                        {unavailabilities.length}
                                    </Badge>
                                )}
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {loading ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2 text-muted small">Cargando horario y disponibilidad...</div>
                        </div>
                    ) : (
                        <Tab.Content className="p-3">
                            {/* PESTAÑA 1: JORNADA SEMANAL */}
                            <Tab.Pane eventKey="weekly">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h6 className="fw-bold mb-1">Horario Laboral Habitual</h6>
                                        <p className="text-muted small mb-0">
                                            Define los días y rangos en los que estás disponible para recibir servicios de Despacho.
                                        </p>
                                    </div>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        onClick={copyMondayToFriday}
                                        className="text-nowrap"
                                    >
                                        📋 Copiar Lun a Vie
                                    </Button>
                                </div>

                                <div className="d-flex flex-column gap-2 mb-3">
                                    {DAYS_OF_WEEK.map(day => {
                                        const config = weeklySchedule[day.key] || { active: false, start: "08:00", end: "18:00" };
                                        return (
                                            <Card key={day.key} className={`border ${config.active ? 'border-primary bg-white' : 'border-light bg-light text-muted'}`}>
                                                <Card.Body className="p-2.5 px-3">
                                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: '130px' }}>
                                                            <Form.Check 
                                                                type="switch"
                                                                id={`switch-${day.key}`}
                                                                checked={config.active}
                                                                onChange={() => handleToggleDay(day.key)}
                                                                label={<span className="fw-bold">{day.name}</span>}
                                                            />
                                                        </div>

                                                        {config.active ? (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="small text-muted">Desde:</span>
                                                                <Form.Control 
                                                                    type="time" 
                                                                    size="sm" 
                                                                    value={config.start || "07:00"}
                                                                    onChange={(e) => handleTimeChange(day.key, 'start', e.target.value)}
                                                                    style={{ width: '110px' }}
                                                                />
                                                                <span className="small text-muted">Hasta:</span>
                                                                <Form.Control 
                                                                    type="time" 
                                                                    size="sm" 
                                                                    value={config.end || "19:00"}
                                                                    onChange={(e) => handleTimeChange(day.key, 'end', e.target.value)}
                                                                    style={{ width: '110px' }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="badge bg-secondary-subtle text-secondary px-3 py-1.5 fw-normal small">
                                                                🏖️ Día Libre Habitual
                                                            </div>
                                                        )}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        );
                                    })}
                                </div>

                                <div className="d-flex justify-content-end">
                                    <Button 
                                        variant="primary" 
                                        onClick={handleSaveSchedule}
                                        disabled={savingSchedule}
                                        className="d-flex align-items-center gap-2 px-4"
                                    >
                                        {savingSchedule ? <Spinner animation="border" size="sm" /> : <CheckCircle2 size={18} />}
                                        Guardar Horario Semanal
                                    </Button>
                                </div>
                            </Tab.Pane>

                            {/* PESTAÑA 2: AUSENCIAS Y VACACIONES */}
                            <Tab.Pane eventKey="absences">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h6 className="fw-bold mb-1">Períodos de Ausencia y Vacaciones</h6>
                                        <p className="text-muted small mb-0">
                                            Programa días libres específicos, vacaciones o mantención de tu vehículo.
                                        </p>
                                    </div>
                                    {!showNewAbsenceForm && (
                                        <Button 
                                            variant="success" 
                                            size="sm" 
                                            onClick={handleStartNewAbsence}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <Plus size={16} /> Programar Ausencia
                                        </Button>
                                    )}
                                </div>

                                {/* FORMULARIO NUEVA AUSENCIA */}
                                {showNewAbsenceForm && (
                                    <Card className="border-primary shadow-sm mb-3">
                                        <Card.Header className="bg-primary text-white py-2 small fw-bold d-flex justify-content-between align-items-center">
                                            <span>➕ Programar Nuevo Período de Ausencia</span>
                                            <Button 
                                                variant="link" 
                                                size="sm" 
                                                className="text-white p-0 text-decoration-none" 
                                                onClick={() => { setShowNewAbsenceForm(false); setConflicts(null); }}
                                            >
                                                ✕ Cancelar
                                            </Button>
                                        </Card.Header>
                                        <Card.Body className="p-3">
                                            <Form>
                                                <div className="row g-2 mb-2">
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label className="small fw-bold mb-1">Fecha Desde:</Form.Label>
                                                        <Form.Control 
                                                            type="date" 
                                                            size="sm" 
                                                            value={startDate} 
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label className="small fw-bold mb-1">Fecha Hasta:</Form.Label>
                                                        <Form.Control 
                                                            type="date" 
                                                            size="sm" 
                                                            value={endDate} 
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row g-2 mb-2">
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label className="small fw-bold mb-1">Motivo:</Form.Label>
                                                        <Form.Select 
                                                            size="sm" 
                                                            value={reason} 
                                                            onChange={(e) => setReason(e.target.value)}
                                                        >
                                                            {ABSENCE_REASONS.map((r, i) => (
                                                                <option key={i} value={r}>{r}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label className="small fw-bold mb-1">Observaciones (Opcional):</Form.Label>
                                                        <Form.Control 
                                                            type="text" 
                                                            size="sm" 
                                                            placeholder="Ej: Viaje fuera de la ciudad" 
                                                            value={notes} 
                                                            onChange={(e) => setNotes(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ALERTA DE CONFLICTOS CON VIAJES PREVIAMENTE ASIGNADOS */}
                                                {conflicts && conflicts.length > 0 && (
                                                    <Alert variant="warning" className="border-2 border-warning mt-3 mb-2 p-2.5">
                                                        <div className="d-flex align-items-center gap-2 fw-bold text-dark mb-1">
                                                            <ShieldAlert className="text-warning" size={20} />
                                                            ¡Atención! Tienes {conflicts.length} viaje(s) asignado(s) en este período:
                                                        </div>
                                                        <div className="small text-muted mb-2">
                                                            Al confirmar tu ausencia, se notificará a Locución y Despacho para que procedan a reasignar estos servicios a otro conductor.
                                                        </div>
                                                        <div className="bg-white rounded border p-2 mb-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                            {conflicts.map((trip) => (
                                                                <div key={trip.id} className="small border-bottom pb-1 mb-1 last-border-0">
                                                                    <strong>Servicio #{trip.id}:</strong> {new Date(trip.scheduled_time).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                                                                    <div className="text-truncate text-muted">{trip.pickup_address} ➔ {trip.dropoff_address}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                onClick={() => setConflicts(null)}
                                                            >
                                                                Modificar Fechas
                                                            </Button>
                                                            <Button 
                                                                variant="danger" 
                                                                size="sm" 
                                                                onClick={executeSaveAbsence}
                                                                disabled={savingAbsence}
                                                                className="fw-bold"
                                                            >
                                                                {savingAbsence ? <Spinner animation="border" size="sm" /> : "Entendido, Confirmar y Notificar a Despacho"}
                                                            </Button>
                                                        </div>
                                                    </Alert>
                                                )}

                                                {!conflicts && (
                                                    <div className="d-flex justify-content-end gap-2 mt-3">
                                                        <Button 
                                                            variant="secondary" 
                                                            size="sm" 
                                                            onClick={() => setShowNewAbsenceForm(false)}
                                                        >
                                                            Cancelar
                                                        </Button>
                                                        <Button 
                                                            variant="primary" 
                                                            size="sm" 
                                                            onClick={handleCheckConflictsAndSave}
                                                            disabled={checkingConflicts || savingAbsence}
                                                        >
                                                            {checkingConflicts ? <Spinner animation="border" size="sm" /> : "Guardar Ausencia"}
                                                        </Button>
                                                    </div>
                                                )}
                                            </Form>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* LISTA DE AUSENCIAS REGISTRADAS */}
                                {unavailabilities.length === 0 ? (
                                    <div className="text-center p-4 bg-light rounded border text-muted small">
                                        No tienes períodos de ausencia ni vacaciones programadas.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {unavailabilities.map((u) => {
                                            const isFuture = new Date(u.end_date) >= new Date();
                                            return (
                                                <Card key={u.id} className={`border ${isFuture ? 'border-info' : 'border-light bg-light'}`}>
                                                    <Card.Body className="p-3 d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Badge bg={u.reason === 'Vacaciones' ? 'success' : 'info'} pill>
                                                                    {u.reason}
                                                                </Badge>
                                                                <span className="fw-bold" style={{ fontSize: '14px' }}>
                                                                    {formatDateStr(u.start_date)} al {formatDateStr(u.end_date)}
                                                                </span>
                                                            </div>
                                                            {u.notes && (
                                                                <div className="small text-muted mt-1">
                                                                    📝 {u.notes}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Button 
                                                            variant="outline-danger" 
                                                            size="sm" 
                                                            onClick={() => handleDeleteAbsence(u.id)}
                                                            title="Eliminar este período"
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    )}
                </Tab.Container>
            </Modal.Body>

            <Modal.Footer className="bg-light p-2">
                <Button variant="secondary" size="sm" onClick={onHide} className="px-4">
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DriverAvailabilityModal;
